import * as Sentry from "@sentry/react";

// Check if we're in development/local environment
const isDevelopment =
  import.meta.env.DEV ||
  import.meta.env.MODE === "development" ||
  import.meta.env.VITE_NODE_ENV === "development";

// Get DSN from environment variable
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

// Check if Sentry should be enabled (has DSN and not in development)
const isSentryEnabled = Boolean(sentryDsn) && !isDevelopment;

// Sentry configuration
export const sentryConfig = {
  dsn: sentryDsn,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  autoSessionTracking: true,
  integrations: [Sentry.browserTracingIntegration()],
  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost"],
  // Environment
  environment: import.meta.env.MODE || "development",
  // Release version from environment
  release: import.meta.env.VITE_APP_VERSION || "1.0.0",
  // Disable Sentry if no DSN or in development
  enabled: isSentryEnabled,
};

// Initialize Sentry with the configuration
export const initSentry = () => {
  // Only initialize Sentry if DSN is provided and not in development
  if (isSentryEnabled) {
    Sentry.init(sentryConfig);
  }
};

// Helper functions for manual error reporting
export const captureException = (error, context = {}) => {
  if (!isSentryEnabled) {
    return;
  }
  Sentry.withScope((scope) => {
    Object.keys(context).forEach((key) => {
      scope.setContext(key, context[key]);
    });
    Sentry.captureException(error);
  });
};

export const captureMessage = (message, level = "info", context = {}) => {
  if (!isSentryEnabled) {
    return;
  }
  Sentry.withScope((scope) => {
    Object.keys(context).forEach((key) => {
      scope.setContext(key, context[key]);
    });
    Sentry.captureMessage(message, level);
  });
};

export const addBreadcrumb = (breadcrumb) => {
  if (!isSentryEnabled) {
    return;
  }
  Sentry.addBreadcrumb(breadcrumb);
};

export const setUser = (user) => {
  if (!isSentryEnabled) {
    return;
  }
  Sentry.setUser(user);
};

export const setTag = (key, value) => {
  if (!isSentryEnabled) {
    return;
  }
  Sentry.setTag(key, value);
};

export const setContext = (key, context) => {
  if (!isSentryEnabled) {
    return;
  }
  Sentry.setContext(key, context);
};
